<template>
  <div class="cms-menu-form">
    <cms-form-header
      :title="isEditing ? $t('cms.menus.editMenu') : $t('cms.menus.createMenu')"
      :disabled="isSaving || isLoadingMenu"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.menus.generalInfo')">
          <div class="cms-form-grid">
            <cms-select
              v-model="form.menu_type"
              :label="$t('cms.menus.menuType')"
              :options="menuTypeOptions"
              :placeholder="$t('cms.menus.menuType')"
            />
            <cms-select
              v-if="form.menu_type === 'page'"
              v-model="pageSelectValue"
              :label="$t('cms.menus.pageLink')"
              :options="pageOptions"
              :placeholder="$t('cms.menus.pageLinkPlaceholder')"
            />
            <div v-if="form.menu_type === 'activity'" class="cms-form-group">
              <label class="cms-input-label">{{ $t('cms.menus.activityLink') }}</label>
              <v-select
                v-model="activitySelectValue"
                :options="activityOptions"
                class="select-search"
                :searchable="true"
                :clearable="true"
                :placeholder="$t('cms.menus.activityLinkPlaceholder')"
                @search="onActivitySearch"
                label="label"
                :reduce="(option: any) => option.value"
              >
                <template v-slot:no-options>
                  {{ $t("clientTour.templateNoOptions") || "No activities found" }}
                </template>
                <template v-slot:option="option">
                  <div class="d-center">
                    <span>{{ option?.label }}</span>
                  </div>
                </template>
                <template v-slot:selected-option="option">
                  <div class="selected d-center py-8">
                    <span>{{ option?.label }}</span>
                  </div>
                </template>
              </v-select>
            </div>
            <cms-input
              v-if="form.menu_type === 'link'"
              v-model="form.external_url"
              :label="$t('cms.menus.externalUrl')"
              :placeholder="$t('cms.menus.externalUrlPlaceholder')"
              :full-width="true"
            />
            <cms-input
              v-if="form.menu_type === 'category'"
              v-model="form.category_slug"
              :label="$t('cms.menus.categorySlug')"
              :placeholder="$t('cms.menus.categorySlugPlaceholder')"
              :full-width="true"
            />
            <cms-select
              v-model="parentSelectValue"
              :label="$t('cms.menus.parentMenu')"
              :options="parentMenuOptions"
              :placeholder="$t('cms.menus.parentMenuPlaceholder')"
              :help-text="$t('cms.menus.parentMenuHelp')"
            />
            <cms-input
              v-model.number="form.order"
              type="number"
              :label="$t('cms.menus.order')"
              :help-text="$t('cms.menus.orderHelp')"
            />
          </div>
        </cms-form-section>

        <cms-form-section :title="$t('cms.menus.translationsSection')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).label"
                  :label="`${$t('cms.menus.menuLabel')} (${locale.toUpperCase()})`"
                  :help-text="$t('cms.menus.menuLabelHelp')"
                  :full-width="true"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>
      </form>

      <template #sidebar>
        <cms-sidebar>
          <cms-sidebar-section :title="$t('cms.menus.publishing')">
            <label class="cms-checkbox-item">
              <input v-model="form.is_public" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.stories.isPublic') }}</span>
            </label>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
    </cms-form-layout>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import vSelect from 'vue-select';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsSelect,
  CmsSidebar,
  CmsSidebarSection,
  CmsTranslationTabs,
} from '@/components/cms';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface MenuTranslation {
  locale: string;
  label: string;
}

interface MenuForm {
  parent_id: number | null;
  cms_page_id: number | null;
  cms_activity_id: number | null;
  menu_type: 'page' | 'link' | 'category' | 'activity';
  external_url: string;
  category_slug: string;
  is_public: boolean;
  order: number;
  translations: MenuTranslation[];
}

interface SelectOption {
  value: number | string;
  label: string;
}

export default defineComponent({
  name: 'TheCmsMenuForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsSelect,
    CmsSidebar,
    CmsSidebarSection,
    CmsTranslationTabs,
    vSelect,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const menuId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => menuId.value !== null);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): MenuTranslation => ({
      locale,
      label: '',
    });

    const form = reactive<MenuForm>({
      parent_id: null,
      cms_page_id: null,
      cms_activity_id: null,
      menu_type: 'page',
      external_url: '',
      category_slug: '',
      is_public: true,
      order: 0,
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
    });

    const pages = ref<SelectOption[]>([]);
    const pageOptions = computed(() => pages.value);

    const activities = ref<SelectOption[]>([]);
    const activityOptions = computed(() => activities.value);
    const isLoadingActivities = ref(false);

    const menus = ref<SelectOption[]>([]);
    const parentMenuOptions = computed(() => {
      // Filter out the current menu if editing
      return menus.value.filter((menu) => menu.value !== menuId.value);
    });

    const menuTypeOptions = computed(() => [
      { value: 'page', label: t('cms.menus.menuTypeOptions.page') },
      { value: 'link', label: t('cms.menus.menuTypeOptions.link') },
      { value: 'category', label: t('cms.menus.menuTypeOptions.category') },
      { value: 'activity', label: t('cms.menus.menuTypeOptions.activity') },
    ]);

    const pageSelectValue = computed({
      get: () => (form.cms_page_id !== null ? form.cms_page_id : ''),
      set: (value: number | string) => {
        if (value === '' || value === null) {
          form.cms_page_id = null;
          return;
        }
        if (typeof value === 'number') {
          form.cms_page_id = value;
          return;
        }
        const parsed = Number(value);
        form.cms_page_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const parentSelectValue = computed({
      get: () => (form.parent_id !== null ? form.parent_id : ''),
      set: (value: number | string) => {
        if (value === '' || value === null) {
          form.parent_id = null;
          return;
        }
        if (typeof value === 'number') {
          form.parent_id = value;
          return;
        }
        const parsed = Number(value);
        form.parent_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const activitySelectValue = computed({
      get: () => {
        // With reduce, v-select stores the reduced value (number)
        // It will find the matching option from activities.value to display
        if (form.cms_activity_id === null) {
          return null;
        }
        // Ensure the activity option exists in the array for display
        const existingOption = activities.value.find(a => a.value === form.cms_activity_id);
        if (!existingOption && form.cms_activity_id) {
          // If option not found, try to load it
          loadActivityById(form.cms_activity_id);
        }
        return form.cms_activity_id;
      },
      set: (value: any) => {
        // With reduce, v-select passes the reduced value (number) directly
        if (!value || value === '' || value === null) {
          form.cms_activity_id = null;
          return;
        }
        if (typeof value === 'number') {
          form.cms_activity_id = value;
          // Ensure the selected activity is in the activities array
          const existing = activities.value.find(a => a.value === value);
          if (!existing) {
            loadActivityById(value);
          }
        } else if (typeof value === 'object' && value.value !== undefined) {
          // Fallback: handle option object if reduce doesn't work as expected
          form.cms_activity_id = value.value;
          // Ensure the selected activity is in the activities array
          if (!activities.value.find(a => a.value === value.value)) {
            activities.value.push(value);
          }
        } else {
          const parsed = Number(value);
          form.cms_activity_id = Number.isNaN(parsed) ? null : parsed;
        }
      },
    });

    const getTranslation = (locale: string): MenuTranslation => {
      let translation = form.translations.find((entry) => entry.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      return translation;
    };

    const requiredTranslationFields: Array<keyof MenuTranslation> = ['label'];

    const translationsAreComplete = computed(() => {
      return form.translations.some((translation) =>
        requiredTranslationFields.every((field) => {
          const value = translation[field];
          if (typeof value === 'string') {
            return value.trim().length > 0;
          }
          return false;
        }),
      );
    });

    const validateTranslations = () => {
      if (translationsAreComplete.value) {
        return true;
      }
      toast.error(t('cms.menus.translationValidationError') || 'Please complete all required fields');
      return false;
    };

    const validateForm = () => {
      if (form.menu_type === 'page' && !form.cms_page_id) {
        toast.error(t('cms.menus.pageRequired') || 'Please select a page');
        return false;
      }
      if (form.menu_type === 'activity' && !form.cms_activity_id) {
        toast.error(t('cms.menus.activityRequired') || 'Please select an activity');
        return false;
      }
      if (form.menu_type === 'link' && !form.external_url.trim()) {
        toast.error(t('cms.menus.externalUrlRequired') || 'Please enter an external URL');
        return false;
      }
      if (form.menu_type === 'category' && !form.category_slug.trim()) {
        toast.error(t('cms.menus.categorySlugRequired') || 'Please enter a category slug');
        return false;
      }
      return true;
    };

    const isSaving = ref(false);
    const isLoadingMenu = ref(false);

    const extractListData = (response: any) => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response?.data?.data?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const extractMenuData = (response: any) => {
      if (!response) return null;
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (candidate.attributes && typeof candidate.attributes === 'object') {
          return {
            id: candidate.id ?? candidate.attributes.id ?? null,
            ...candidate.attributes,
            relationships: candidate.relationships,
          };
        }
        if (candidate.id || candidate.translations) {
          return candidate;
        }
      }
      return null;
    };

    const mapMenuToForm = (menu: any) => {
      form.parent_id = menu.parent_id || null;
      form.cms_page_id = menu.cms_page_id || menu.page?.id || null;
      form.cms_activity_id = menu.cms_activity_id || menu.activity?.id || null;
      form.menu_type = menu.menu_type || 'page';
      form.external_url = menu.external_url || '';
      form.category_slug = menu.category_slug || '';
      form.is_public = !!menu.is_public;
      form.order = menu.order || 0;
    };

    const applyTranslationsFromMenu = (menu: any, locale: string) => {
      const translations = extractRelationship(menu, 'translations');
      const normalized = normalizeResource(translations);
      const translation = Array.isArray(normalized) ? normalized[0] : normalized || menu.translations?.[0];
      if (!translation) {
        return;
      }
      const target = getTranslation(locale);
      target.label = translation.label || '';
    };

    const buildPayload = () => {
      const translationsPayload = form.translations
        .map((translation) => ({
          locale: translation.locale,
          label: translation.label?.trim(),
        }))
        .filter((translation) => translation.label && translation.label.length > 0);

      const payload: Record<string, any> = {
        menu_type: form.menu_type,
        is_public: form.is_public,
        order: form.order,
        translations: translationsPayload,
      };

      if (form.parent_id !== null) {
        payload.parent_id = form.parent_id;
      }

      if (form.menu_type === 'page') {
        payload.cms_page_id = form.cms_page_id;
      } else if (form.menu_type === 'activity') {
        payload.cms_activity_id = form.cms_activity_id;
      } else if (form.menu_type === 'link') {
        payload.external_url = form.external_url.trim();
      } else if (form.menu_type === 'category') {
        payload.category_slug = form.category_slug.trim();
      }

      return payload;
    };

    const extractMenuId = (response: any): number | null => {
      if (!response) return null;
      if (typeof response.id === 'number') {
        return response.id;
      }
      if (typeof response?.data?.id === 'number') {
        return response.data.id;
      }
      if (typeof response?.data?.data?.id === 'number') {
        return response.data.data.id;
      }
      return null;
    };

    const loadPages = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.pageService.listCmsPages(agencySlug.value, {
          perPage: 100,
          filters: { is_public: 1 },
        });
        const list = extractListData(response);
        pages.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translationList = Array.isArray(normalizedTranslations)
            ? normalizedTranslations
            : normalizedTranslations
              ? [normalizedTranslations]
              : [];
          const translation = translationList[0];
          return {
            value: normalized.id,
            label:
              translation?.title ||
              normalized.title ||
              translation?.custom_url ||
              translation?.customUrl ||
              normalized.custom_url ||
              `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load pages', error);
      }
    };

    const onActivitySearch = async (searchTerm: string) => {
      if (!agencySlug.value || !searchTerm || searchTerm.length < 1) {
        // Don't clear activities if one is selected - keep the selected one
        if (form.cms_activity_id) {
          // Keep only the selected activity in the array
          const selected = activities.value.find(a => a.value === form.cms_activity_id);
          activities.value = selected ? [selected] : [];
        } else {
          activities.value = [];
        }
        return;
      }

      isLoadingActivities.value = true;
      try {
        const filters: Record<string, any> = {
          order: { key: 'updated_at', direction: 'desc' },
        };
        
        if (searchTerm.trim()) {
          filters.search = searchTerm.trim();
        }

        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          ...filters,
        });
        const list = extractListData(response);
        const included = response?.included || response?.data?.included || [];
        activities.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translation = Array.isArray(normalizedTranslations) 
            ? normalizedTranslations[0] 
            : normalizedTranslations || null;
          return {
            value: normalized.id,
            label: translation?.title || normalized.title || normalized.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to search activities', error);
        activities.value = [];
        toast.error(t('cms.menus.failedToLoadActivities') || 'Failed to load activities');
      } finally {
        isLoadingActivities.value = false;
      }
    };

    const loadActivityById = async (activityId: number) => {
      if (!agencySlug.value || !activityId) return;
      try {
        const filters: Record<string, any> = {};
        filters.id = activityId;

        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          ...filters,
        });
        const list = extractListData(response);
        if (list.length > 0) {
          const item = list[0];
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translation = Array.isArray(normalizedTranslations) 
            ? normalizedTranslations[0] 
            : normalizedTranslations || null;
          const activityOption = {
            value: normalized.id,
            label: translation?.title || normalized.title || normalized.name || `#${normalized.id}`,
          };
          // Add to activities if not already present
          if (!activities.value.find(a => a.value === activityOption.value)) {
            activities.value.push(activityOption);
          }
        }
      } catch (error) {
        console.error('Failed to load activity', error);
      }
    };

    const loadMenus = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.menuService.listCmsMenus(agencySlug.value, {});
        const list = extractListData(response);
        const flattenMenus = (menuList: any[]): any[] => {
          const result: any[] = [];
          menuList.forEach((menu: any) => {
            const normalized = normalizeResource(menu) || menu;
            const translations = extractRelationship(normalized, 'translations');
            const translation = Array.isArray(translations) ? translations[0] : translations;
            result.push({
              value: normalized.id,
              label: translation?.label || normalized.label || `Menu #${normalized.id}`,
            });
            if (normalized.children && Array.isArray(normalized.children)) {
              result.push(...flattenMenus(normalized.children));
            }
          });
          return result;
        };
        menus.value = flattenMenus(list);
      } catch (error) {
        console.error('Failed to load menus', error);
      }
    };

    const loadMenu = async () => {
      if (!agencySlug.value || !menuId.value) {
        return;
      }
      isLoadingMenu.value = true;
      try {
        for (const locale of locales.value) {
          const response = await api.menuService.getCmsMenu(agencySlug.value, menuId.value, {
            headers: {
              'Accept-Language': locale,
            },
          });
          const menuData = extractMenuData(response);
          if (!menuData) {
            continue;
          }
          if (locale === locales.value[0]) {
            mapMenuToForm(menuData);
          }
          applyTranslationsFromMenu(menuData, locale);
        }
      } catch (error) {
        console.error('Failed to load menu', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingMenu.value = false;
      }
    };

    const persistMenu = async (publish = false) => {
      if (!agencySlug.value) return;
      if (!validateTranslations()) {
        return;
      }
      if (!validateForm()) {
        return;
      }
      const payload = buildPayload();
      isSaving.value = true;
      try {
        if (menuId.value) {
          await api.menuService.updateCmsMenu(agencySlug.value, menuId.value, payload);
        } else {
          const response = await api.menuService.createCmsMenu(agencySlug.value, payload);
          const newId = extractMenuId(response);
          if (newId) {
            menuId.value = newId;
            try {
              await router.replace({ name: 'cms.menus.edit', params: { id: newId } });
            } catch (error) {
              console.warn('Router replace failed', error);
            }
            await loadMenu();
          }
        }
        if (publish && menuId.value) {
          await api.menuService.updateCmsMenu(agencySlug.value, menuId.value, {
            is_public: true,
          });
          form.is_public = true;
        }
        toast.success(publish ? t('cms.menus.publishSuccess') : t('general.shared.savedChanges'));
      } catch (error: any) {
        console.error('Error saving menu', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const saveDraft = () => {
      form.is_public = false;
      persistMenu(false);
    };

    const saveAndPublish = () => {
      form.is_public = true;
      persistMenu(true);
    };

    const handleSubmit = () => {
      saveDraft();
    };

    const goBack = () => {
      router.push({ name: 'cms.menus' });
    };

    watch(
      () => route.params.id,
      (newId) => {
        menuId.value = newId ? Number(newId) : null;
        if (menuId.value) {
          loadMenu();
        } else {
          form.parent_id = null;
          form.cms_page_id = null;
          form.menu_type = 'page';
          form.external_url = '';
          form.category_slug = '';
          form.is_public = true;
          form.order = 0;
          form.translations = locales.value.map((locale) => createEmptyTranslation(locale));
        }
      },
      { immediate: true },
    );

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadPages();
          loadMenus();
          if (menuId.value) {
            loadMenu();
          }
        }
      },
      { immediate: true },
    );

    // Load activity when editing and activity is selected
    watch(
      () => [form.menu_type, form.cms_activity_id],
      ([menuType, activityId]) => {
        if (menuType === 'activity' && activityId && typeof activityId === 'number' && agencySlug.value) {
          // Check if activity is already in the array
          const existing = activities.value.find(a => a.value === activityId);
          if (!existing) {
            loadActivityById(activityId);
          }
        }
      },
      { immediate: true },
    );

    return {
      t,
      form,
      isEditing,
      isSaving,
      isLoadingMenu,
      languages,
      currentLanguage,
      getTranslation,
      menuTypeOptions,
      pageOptions,
      pageSelectValue,
      parentMenuOptions,
      parentSelectValue,
      saveDraft,
      saveAndPublish,
      handleSubmit,
      goBack,
      menuId,
      activityOptions,
      activitySelectValue,
      onActivitySearch,
      isLoadingActivities,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-menu-form {
  color: $cms-text-primary;
}
</style>

